// The Lab's exhibit registry: each entry is one visual explorer of the
// mathematics lurking inside voice leading. Built ones carry a component;
// the rest render as "coming soon" cards on the hub.

import type { ComponentType } from 'react';
import { SynonymsExhibit } from './SynonymsExhibit';
import { WormholesExhibit } from './WormholesExhibit';
import { TonnetzExhibit } from './TonnetzExhibit';
import { CubeDanceExhibit } from './CubeDanceExhibit';
import { CycleMapExhibit } from './CycleMapExhibit';
import { MobiusExhibit } from './MobiusExhibit';
import { EvennessExhibit } from './EvennessExhibit';
import { VSystemExhibit } from './VSystemExhibit';
import { DalSegnoMachineExhibit } from './DalSegnoMachineExhibit';
import { PianoVoicingsExhibit } from './PianoVoicingsExhibit';

export interface LabExhibit {
  id: string;
  title: string;
  tagline: string;
  blurb: string;
  Component?: ComponentType;
}

export const LAB_EXHIBITS: LabExhibit[] = [
  {
    id: 'piano-voicings',
    title: 'Piano voicings',
    tagline: 'Piano voice leading',
    blurb:
      'A ii–V–I voice-led over piano voicings by pitch distance alone. Shows the keys pressed, the common tones held and the total movement against root position.',
    Component: PianoVoicingsExhibit,
  },
  {
    id: 'synonyms',
    title: 'Chord synonyms',
    tagline: 'Chords with the same notes',
    blurb:
      'Cmaj9 has the same notes as Em7: rootless extensions make several chords share one grip. Lists every chord name for a set of notes.',
    Component: SynonymsExhibit,
  },
  {
    id: 'wormholes',
    title: 'Diminished and augmented symmetry',
    tagline: 'Symmetric chords',
    blurb:
      'There are only 3 diminished seventh chords and 4 augmented triads. Each diminished seventh is shared by four dominant seventh chords.',
    Component: WormholesExhibit,
  },
  {
    id: 'tonnetz',
    title: 'Tonnetz',
    tagline: 'P, L and R transformations of triads',
    blurb:
      'All 24 major and minor triads on one grid, connected by moves that change one note. Includes the hexatonic cycles.',
    Component: TonnetzExhibit,
  },
  {
    id: 'cube-dance',
    title: 'Cube Dance',
    tagline: 'Semitone voice-leading graphs',
    blurb:
      'Douthett & Steinbach’s graphs: triads linked through augmented triads and seventh chords through diminished sevenths, one semitone at a time.',
    Component: CubeDanceExhibit,
  },
  {
    id: 'cycle-map',
    title: 'Cycle map',
    tagline: 'Form cycling on the loaded tune',
    blurb:
      'Cycle form maps each opening voicing to the voicing the next pass starts on. Shows the cycles, fixed points and the voicings that lead into each.',
    Component: CycleMapExhibit,
  },
  {
    id: 'mobius',
    title: 'Möbius strip',
    tagline: 'The space of two-note chords',
    blurb:
      'Tymoczko’s model: every two-note chord is a point and every voice leading a path. The space is a Möbius strip.',
    Component: MobiusExhibit,
  },
  {
    id: 'evenness',
    title: 'Maximal evenness',
    tagline: 'Scales and Euclidean rhythms',
    blurb:
      'The diatonic scale, the diminished seventh and the tresillo come from one rule: spread k notes over n slots as evenly as possible.',
    Component: EvennessExhibit,
  },
  {
    id: 'v-system',
    title: 'V-System',
    tagline: 'Ted Greene’s 14 spreads',
    blurb:
      'Every four-note voicing belongs to one of 14 families. Close position is V-1 and drop 2 is V-2.',
    Component: VSystemExhibit,
  },
  {
    id: 'segno-machine',
    title: 'Dal Segno Machine',
    tagline: 'Repeat signs as a program',
    blurb:
      'If a repeat count can refer to another section’s count, rehearsal letters act as registers and D.S. as a conditional jump, so a score can compute. Includes a Collatz example.',
    Component: DalSegnoMachineExhibit,
  },
];
